import {useState, useMemo, useCallback, useEffect, memo} from 'react'
import {
  Lock,
  Unlock,
  ChevronDown,
  ChevronsDown,
  ChevronsUp,
  Bot,
  MessageSquare,
  Eye,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react'
import {
  getPermissionModule,
  getPermissionModuleOrder,
  getCategoryLabel,
  getPermissionCategory,
  getPermissionRisk,
  isModulePermission,
  isModuleScreenPermission,
  isPermissionActive,
  type Permission,
  type PermissionId,
} from './permissionMetadata'

type PermissionListProps = {
  permissions: Permission[]
  allPermissions: Permission[]
  selectedPermissions: PermissionId[]
  onPermissionChange: (permission: Permission, checked: boolean) => void
  onPermissionsChange: (permissions: Permission[], checked: boolean) => void
}

const categoryIcons: any = {
  unilever: '/media/icons/duotune/menu/unilever.svg', // <- agora isso vira imagem automaticamente
  cashback: '💰',
  logs: '📋',
  ai: '🤖',
  users: '👥',
  nps: '📊',
  campaign: '🎯',
  conecta: '🔗',
  datatransfer: '📦',
  reports: '📈',
  api: '🔌',
  briefing: '📣',
  'briefing-topicos': '/media/icons/duotune/menu/briefing-topics/briefing-topics.svg',
  seller: '🛒',
  infrapay: '💳',
  devops: '⚙️',
  settings: '🔧',
  fechamentos: '📑',
}

function isImageUrlLike(value: unknown): value is string {
  if (typeof value !== 'string') return false

  // aceita /media/..., ./..., ../..., http(s)...
  const looksLikePathOrUrl =
    value.startsWith('http://') ||
    value.startsWith('https://') ||
    value.startsWith('/') ||
    value.startsWith('./') ||
    value.startsWith('../')

  if (!looksLikePathOrUrl) return false

  // extensões mais comuns (inclui svg)
  return /\.(svg|png|jpe?g|webp|gif)(\?.*)?$/i.test(value)
}

const CategoryIcon = memo(({icon, alt}: {icon: unknown; alt: string}) => {
  const [imgError, setImgError] = useState(false)

  // Se for URL/caminho de imagem e não deu erro, renderiza <img/>
  if (!imgError && isImageUrlLike(icon)) {
    return (
      <img
        src={icon}
        alt={alt}
        className='w-8 h-8 object-contain'
        loading='lazy'
        onError={() => setImgError(true)}
      />
    )
  }

  // Caso contrário, renderiza como texto (emoji)
  return (
    <span className='text-2xl leading-none' aria-label={alt} title={alt}>
      {(typeof icon === 'string' && icon) || '📁'}
    </span>
  )
})
CategoryIcon.displayName = 'CategoryIcon'

const PermissionItem = memo(
  ({
    permission,
    isChecked,
    onPermissionChange,
    isFirst,
  }: {
    permission: Permission
    isChecked: boolean
    onPermissionChange: (permission: Permission, checked: boolean) => void
    isFirst: boolean
  }) => {
    const risk = getPermissionRisk(permission.name)
    const modulePermission = isModulePermission(permission)
    const active = isPermissionActive(permission)

    return (
      <label
        className={`flex items-center px-6 py-4 transition-colors cursor-pointer group ${
          modulePermission ? 'hover:bg-blue-100/70' : 'hover:bg-blue-50'
        } ${isFirst ? 'border-t border-gray-200' : ''}`}
      >
        <div className='flex items-center gap-4 w-full'>
          <div className='relative flex items-center justify-center'>
            <input
              type='checkbox'
              checked={isChecked}
              onChange={(e) => onPermissionChange(permission, e.target.checked)}
              className='w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-200 focus:ring-offset-0 cursor-pointer transition-all'
            />
          </div>
          <div className='flex-1'>
            <span
              className={`text-sm font-medium transition-colors ${
                isChecked ? 'text-blue-700' : 'text-gray-700 group-hover:text-blue-600'
              }`}
            >
              {permission.title}
            </span>
            <span
              className='mt-1 block truncate font-mono text-[11px] text-gray-400'
              title={permission.name}
            >
              {permission.name}
            </span>
            {risk !== 'standard' && (
              <div className='mt-1.5'>
                {risk === 'critical' ? (
                  <span className='inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-700'>
                    <AlertTriangle className='h-3 w-3' />
                    Crítica
                  </span>
                ) : (
                  <span className='inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700'>
                    <Lock className='h-3 w-3' />
                    Sensível
                  </span>
                )}
              </div>
            )}
          </div>
          <div className='flex items-center gap-2'>
            {!active && (
              <span className='rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-700'>Inativa no sistema</span>
            )}
            {isChecked ? (
              <div className='flex items-center gap-2 px-3 py-1 bg-green-100 rounded-full'>
                <Unlock className='w-4 h-4 text-green-600' />
                <span className='text-xs font-semibold text-green-700'>Selecionada</span>
              </div>
            ) : (
              <div className='flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full opacity-0 group-hover:opacity-100 transition-opacity'>
                <Lock className='w-4 h-4 text-gray-500' />
                <span className='text-xs font-medium text-gray-600'>Não selecionada</span>
              </div>
            )}
          </div>
        </div>
      </label>
    )
  }
)

PermissionItem.displayName = 'PermissionItem'

const CategorySection = memo(
  ({
    category,
    perms,
    allPermissions,
    isExpanded,
    selectedCount,
    totalCount,
    allSelected,
    someSelected,
    selectedSet,
    onToggleCategory,
    onPermissionChange,
    onPermissionsChange,
  }: {
    category: string
    perms: Permission[]
    allPermissions: Permission[]
    isExpanded: boolean
    selectedCount: number
    totalCount: number
    allSelected: boolean
    someSelected: boolean
    selectedSet: Set<PermissionId>
    onToggleCategory: (category: string) => void
    onPermissionChange: (permission: Permission, checked: boolean) => void
    onPermissionsChange: (permissions: Permission[], checked: boolean) => void
  }) => {
    const label = getCategoryLabel(category)
    const agentStudio = category === 'agent-studio'
    const groupedModule = agentStudio || category === 'platform-review'
    const categoryPermissions = allPermissions.filter(
      (permission) => getPermissionCategory(permission.name) === category
    )
    const screenPermissions = categoryPermissions.filter((permission) =>
      isModuleScreenPermission(permission.name)
    )

    const permissionModules = Object.values(
      perms.reduce((acc, permission) => {
        const module = getPermissionModule(permission.name)
        if (!acc[module.key]) acc[module.key] = {module, permissions: []}
        acc[module.key].permissions.push(permission)
        return acc
      }, {} as Record<string, {module: ReturnType<typeof getPermissionModule>; permissions: Permission[]}>)
    ).sort(
      (a, b) =>
        getPermissionModuleOrder(a.permissions[0].name) -
        getPermissionModuleOrder(b.permissions[0].name)
    )
    return (
      <section
        aria-label={label}
        className='bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden transition-shadow duration-200 hover:shadow-md'
      >
        <button
          onClick={() => onToggleCategory(category)}
          className='w-full bg-gradient-to-r px-6 py-4 flex items-center justify-between hover:from-gray-100 hover:to-gray-200 transition-colors duration-200'
        >
          <div className='flex items-center gap-3'>
            {groupedModule ? (
              <span className='flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white shadow-sm'>
                {agentStudio ? <Bot className='h-6 w-6' /> : <MessageSquare className='h-6 w-6' />}
              </span>
            ) : (
              <CategoryIcon icon={categoryIcons[category]} alt={label} />
            )}

            <div className='flex flex-col items-start'>
              <h3 className='text-lg font-bold text-gray-800'>{label}</h3>
              <div className='flex items-center gap-2 mt-1'>
                <span className='text-xs text-gray-500'>
                  {totalCount} {totalCount === 1 ? 'permissão' : 'permissões'}
                </span>
                {selectedCount > 0 && (
                  <span
                    className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                      allSelected ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                    }`}
                  >
                    {selectedCount} selecionada{selectedCount !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className='flex items-center gap-3'>
            {someSelected && <div className='w-3 h-3 bg-blue-500 rounded-full animate-pulse'></div>}
            {allSelected && <div className='w-3 h-3 bg-green-500 rounded-full'></div>}
            <div
              className={`transform transition-transform duration-200 ${
                isExpanded ? 'rotate-0' : '-rotate-90'
              }`}
            >
              <ChevronDown className='w-5 h-5 text-gray-600' />
            </div>
          </div>
        </button>

        {groupedModule && (
          <div className='flex flex-col gap-3 border-t border-blue-200 bg-blue-50 px-5 py-4 lg:flex-row lg:items-center lg:justify-between'>
            <div>
              <h4 className='text-sm font-bold text-gray-900'>
                Acesso organizado por telas e ações
              </h4>
              <p className='text-xs text-gray-600'>
                Dependências necessárias são adicionadas automaticamente.
              </p>
            </div>
            <div className='flex flex-wrap gap-2'>
              <button
                type='button'
                onClick={() => onPermissionsChange(screenPermissions, true)}
                className='inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100'
              >
                <Eye className='h-4 w-4' /> Adicionar telas
              </button>
              <button
                type='button'
                onClick={() => onPermissionsChange(categoryPermissions, true)}
                className='inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700'
              >
                <CheckCircle2 className='h-4 w-4' /> Acesso completo
              </button>
              <button
                type='button'
                onClick={() => onPermissionsChange(categoryPermissions, false)}
                className='rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-100'
              >
                Limpar
              </button>
            </div>
          </div>
        )}

        {isExpanded && (
          <div className='divide-y divide-gray-100 bg-white'>
            {groupedModule ? (
              <div className='space-y-3 bg-slate-50 p-4'>
                {permissionModules.map(({module, permissions: modulePermissions}) => (
                  <div
                    key={module.key}
                    className='overflow-hidden rounded-xl border border-gray-200 bg-white'
                  >
                    <div className='flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-3'>
                      <div>
                        <h4 className='text-sm font-bold text-gray-900'>{module.label}</h4>
                        <p className='mt-0.5 text-xs text-gray-500'>{module.description}</p>
                      </div>
                      <button
                        type='button'
                        onClick={() =>
                          onPermissionsChange(
                            modulePermissions,
                            !modulePermissions.every((permission) =>
                              selectedSet.has(permission.id_feature)
                            )
                          )
                        }
                        className='shrink-0 rounded-lg bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100'
                      >
                        {modulePermissions.every((permission) =>
                          selectedSet.has(permission.id_feature)
                        )
                          ? 'Limpar módulo'
                          : 'Selecionar módulo'}
                      </button>
                    </div>
                    <div className='divide-y divide-gray-100'>
                      {modulePermissions.map((permission, index) => (
                        <PermissionItem
                          key={permission.id_feature}
                          permission={permission}
                          isChecked={selectedSet.has(permission.id_feature)}
                          onPermissionChange={onPermissionChange}
                          isFirst={index === 0}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              perms.map((permission, index) => (
                <PermissionItem
                  key={permission.id_feature}
                  permission={permission}
                  isChecked={selectedSet.has(permission.id_feature)}
                  onPermissionChange={onPermissionChange}
                  isFirst={index === 0}
                />
              ))
            )}
          </div>
        )}
      </section>
    )
  }
)

CategorySection.displayName = 'CategorySection'

export const PermissionList = ({
  permissions,
  allPermissions,
  selectedPermissions,
  onPermissionChange,
  onPermissionsChange,
}: PermissionListProps) => {
  const groupedPermissions = useMemo(() => {
    return permissions.reduce((acc, permission) => {
      const category = getPermissionCategory(permission.name)
      if (!acc[category]) {
        acc[category] = []
      }
      acc[category].push(permission)
      return acc
    }, {} as Record<string, Permission[]>)
  }, [permissions])

  const categories = useMemo(
    () =>
      Object.keys(groupedPermissions).sort((a, b) => {
        if (a === 'agent-studio') return -1
        if (b === 'agent-studio') return 1
        return getCategoryLabel(a).localeCompare(getCategoryLabel(b), 'pt-BR')
      }),
    [groupedPermissions]
  )

  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>(() =>
    categories.reduce((acc, category) => {
      acc[category] = category === 'agent-studio' || category === 'platform-review'
      return acc
    }, {} as Record<string, boolean>)
  )

  const selectedSet = useMemo(() => new Set(selectedPermissions), [selectedPermissions])

  useEffect(() => {
    setExpandedCategories((current) => {
      const next = {...current}
      categories.forEach((category) => {
        if (next[category] === undefined) {
          next[category] = category === 'agent-studio' || category === 'platform-review'
        }
      })
      return next
    })
  }, [categories])

  const categoryStats = useMemo(() => {
    return categories.reduce((acc, category) => {
      const perms = groupedPermissions[category]
      const selectedCount = perms.filter((p) => selectedSet.has(p.id_feature)).length
      acc[category] = {
        total: perms.length,
        selected: selectedCount,
        allSelected: selectedCount === perms.length,
        someSelected: selectedCount > 0 && selectedCount < perms.length,
      }
      return acc
    }, {} as Record<string, {total: number; selected: number; allSelected: boolean; someSelected: boolean}>)
  }, [categories, groupedPermissions, selectedSet])

  const toggleCategory = useCallback((category: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }))
  }, [])

  const toggleAll = useCallback(
    (expand: boolean) => {
      setExpandedCategories(
        categories.reduce((acc, category) => {
          acc[category] = expand
          return acc
        }, {} as Record<string, boolean>)
      )
    },
    [categories]
  )

  const allExpanded = useMemo(
    () => categories.every((cat) => expandedCategories[cat]),
    [expandedCategories, categories]
  )

  const allCollapsed = useMemo(
    () => categories.every((cat) => !expandedCategories[cat]),
    [expandedCategories, categories]
  )

  return (
    <div className='space-y-4'>
      <div className='flex justify-end gap-2 mb-4'>
        <button
          onClick={() => toggleAll(true)}
          disabled={allExpanded}
          className='flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
        >
          <ChevronsDown className='w-4 h-4' />
          Expandir Todos
        </button>
        <button
          onClick={() => toggleAll(false)}
          disabled={allCollapsed}
          className='flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
        >
          <ChevronsUp className='w-4 h-4' />
          Colapsar Todos
        </button>
      </div>

      {categories.map((category) => {
        const stats = categoryStats[category]
        return (
          <CategorySection
            key={category}
            category={category}
            perms={groupedPermissions[category]}
            allPermissions={allPermissions}
            isExpanded={!!expandedCategories[category]}
            selectedCount={stats.selected}
            totalCount={stats.total}
            allSelected={stats.allSelected}
            someSelected={stats.someSelected}
            selectedSet={selectedSet}
            onToggleCategory={toggleCategory}
            onPermissionChange={onPermissionChange}
            onPermissionsChange={onPermissionsChange}
          />
        )
      })}
    </div>
  )
}
